import { gql } from '@apollo/client';

import { client } from '@/services/apollo';
import { type RecoilUserSettersType, COMMON_USER_FIELDS } from '@/types/globals';

const UPDATE_USER = gql`
    mutation UpdateUser($updateUserInput: UpdateUserInput!) {
        updateUser(input: $updateUserInput) {
            clientMutationId
            user {
                ${COMMON_USER_FIELDS}
            }
            errors {
                path
                message
            }
        }
    }
`;

export interface SocialLinks {
    linkedIn: string,
    xTwitter: string,
    facebook: string,
    instagram: string,
    gitHub: string,
    website: string,
    other_1: string,
    other_2: string,
}

export interface WorkHistoryItem {
    id: string;
    title: string;
    organization: string;
    description: string;
    displayOnBrief: boolean;

    // In graphql they are dates, but it returns strings
    startAt: string;
    endAt?: string;

    createdAt: string;
    updatedAt: string;
}

export interface UpdateUserAttributesType {
    fullName: string,
    industry: string,
    occupation: string,
    role: string,
    company: string,
    birthday: Date,
    timezoneName: string,
    timezoneOffset: number,
    recognizeSameDomainUsers: boolean,
    enableArchival: boolean,
    slowArchivalPeriod: number,
    fastArchivalPeriod: number,
    enableYesterdaysInbox: boolean,
    vacationMode: boolean,
    bioOnly: boolean,
    communicationPreferences: string,
    meetingPreferences: string,
    socialLinks: SocialLinks,
    profileImageUrl: JSON,
    // rawBioKey: string,
}

export interface SendUserBirthdayAttributesType {
    userId: string,
}

async function updateUser(
    fullName: string,
    industry: string,
    occupation: string,
    role: string,
    birthday: Date,
    timezoneName: string,
    timezoneOffset: number,
    recognizeSameDomainUsers: boolean,
    slowArchivalPeriod: number,
    fastArchivalPeriod: number,
    enableArchival: boolean,
    enableYesterdaysInbox: boolean,
    receiveProductUpdates: boolean,
    usesSuperhuman: boolean,
    usesSanebox: boolean,
    setters: RecoilUserSettersType,
    cb: (data: any, error: any) => void,
) {
    const attributes = {
        fullName,
        industry,
        occupation,
        role,
        birthday,
        timezoneName,
        timezoneOffset,
        recognizeSameDomainUsers,
        slowArchivalPeriod,
        fastArchivalPeriod,
        enableArchival,
        enableYesterdaysInbox,
        receiveProductUpdates,
        usesSuperhuman,
        usesSanebox,
    } as Partial<UpdateUserAttributesType>;

    try {
        const response = await client.mutate({
            mutation: UPDATE_USER,
            variables: {
                updateUserInput: {
                    attributes,
                },
            },
        });
        if (response) {
            if (response.data.updateUser.user) {
                setters.setUser(response.data.updateUser.user);
                setters.setInitialized(true);
            }
            cb(response.data, null);
        }
    } catch (error) {
        cb(null, error);
    }
}

async function updateBirthdaySettings(
    birthdayEmailConfig: {[key: string]: any},
    setters: RecoilUserSettersType,
    cb: (data: any, error: any) => void,
) {
    const attributes = {
        ...birthdayEmailConfig,
    } as Partial<UpdateUserAttributesType>;

    try {
        const response = await client.mutate({
            mutation: UPDATE_USER,
            variables: {
                updateUserInput: {
                    attributes,
                },
            },
        });
        if (response) {
            if (response.data.updateUser.user) {
                setters.setUser(response.data.updateUser.user);
                setters.setInitialized(true);
            }
            cb(response.data, null);
        }
    } catch (error) {
        cb(null, error);
    }
}

export const SEND_BIRTHDAY = gql`
    mutation SendBirthdayMailNow {
        sendBirthdayMailNow(input: {}) {
            clientMutationId
            errors {
                path
                message
            }
        }
    }
`;

async function sendBirthdayMailNow(
    cb: (data: any, error: any) => void,
) {
    try {
        const response = await client.mutate({
            mutation: SEND_BIRTHDAY,
            variables: {
            },
        });
        if (response) {
            cb(response.data, null);
        }
    } catch (error) {
        cb(null, error);
    }
}

const SUSPEND_USER = gql`
    mutation SuspendUser {
        suspendUser(input: {}) {
            clientMutationId
            user {
                ${COMMON_USER_FIELDS}
            }
            errors {
                path
                message
            }
        }
    }
`;

async function suspendUser(
    setters: RecoilUserSettersType,
    cb: (data: any, error: any) => void,
) {
    try {
        const response = await client.mutate({
            mutation: SUSPEND_USER,
            variables: {},
        });
        if (response) {
            if (response.data.suspendUser.user) {
                setters.setUser(response.data.suspendUser.user);
            }
            cb(response.data, null);
        }
    } catch (error) {
        cb(null, error);
    }
}

const REACTIVATE_USER = gql`
    mutation ReactivateUser {
        reactivateUser(input: {}) {
            clientMutationId
            user {
                ${COMMON_USER_FIELDS}
            }
            errors {
                path
                message
            }
        }
    }
`;

async function reactivateUser(
    setters: RecoilUserSettersType,
    cb: (data: any, error: any) => void,
) {
    try {
        const response = await client.mutate({
            mutation: REACTIVATE_USER,
            variables: {},
        });
        if (response) {
            if (response.data.reactivateUser.user) {
                setters.setUser(response.data.reactivateUser.user);
            }
            cb(response.data, null);
        }
    } catch (error) {
        cb(null, error);
    }
}

const DELETE_USER = gql`
    mutation DeleteUser {
        deleteUser(input: {}) {
            clientMutationId
            user {
                ${COMMON_USER_FIELDS}
            }
            errors {
                path
                message
            }
        }
    }
`;

async function deleteUser(
    setters: RecoilUserSettersType,
    cb: (data: any, error: any) => void,
) {
    try {
        const response = await client.mutate({
            mutation: DELETE_USER,
            variables: {},
        });
        if (response) {
            if (response.data.deleteUser.user) {
                setters.setUser(response.data.deleteUser.user);
            }
            cb(response.data, null);
        }
    } catch (error) {
        cb(null, error);
    }
}

export {
    updateUser, suspendUser, reactivateUser, deleteUser, UPDATE_USER, sendBirthdayMailNow,
    updateBirthdaySettings,
};
